import React, { useState, useEffect } from 'react'
import { 
  BarChart3, 
  TrendingUp, 
  Calendar, 
  Download,
  AlertCircle,
  Filter
} from 'lucide-react'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts'
import { reportsAPI } from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import LoadingSpinner from '../components/LoadingSpinner'
import SortableTable from '../components/SortableTable'
import toast from 'react-hot-toast'

const Reports = () => {
  const { user } = useAuth()
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('performance')
  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  })
  const [data, setData] = useState({
    performance: [],
    instruments: [],
    strategies: [],
    monthlySummary: null
  })

  useEffect(() => {
    if (user?.kiteConnected) {
      fetchReports()
    } else {
      setLoading(false)
    }
  }, [user, dateRange])

  const fetchReports = async () => {
    try {
      const [performanceRes, instrumentsRes, strategiesRes, summaryRes] = await Promise.allSettled([
        reportsAPI.getPerformance({ 
          startDate: dateRange.startDate, 
          endDate: dateRange.endDate,
          groupBy: 'day'
        }),
        reportsAPI.getInstruments({ 
          startDate: dateRange.startDate, 
          endDate: dateRange.endDate 
        }),
        reportsAPI.getStrategies({ 
          startDate: dateRange.startDate, 
          endDate: dateRange.endDate 
        }),
        reportsAPI.getMonthlySummary()
      ])

      setData({
        performance: performanceRes.status === 'fulfilled' ? performanceRes.value.data : [],
        instruments: instrumentsRes.status === 'fulfilled' ? instrumentsRes.value.data : [],
        strategies: strategiesRes.status === 'fulfilled' ? strategiesRes.value.data : [],
        monthlySummary: summaryRes.status === 'fulfilled' ? summaryRes.value.data : null
      })
    } catch (error) {
      console.error('Reports fetch error:', error)
      toast.error('Failed to fetch reports')
    } finally {
      setLoading(false)
    }
  }

  const handleDateChange = (field, value) => {
    setDateRange(prev => ({
      ...prev,
      [field]: value
    }))
  }

  if (!user?.kiteConnected) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Kite Not Connected</h2>
        <p className="text-gray-600 mb-6">
          Please connect your Kite account in Settings to view reports.
        </p>
        <a href="/settings" className="btn-primary">
          Go to Settings
        </a>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="large" />
      </div>
    )
  }

  const tabs = [
    { id: 'performance', name: 'Performance', icon: TrendingUp },
    { id: 'instruments', name: 'Instruments', icon: BarChart3 },
    { id: 'strategies', name: 'Strategies', icon: Filter }
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Reports</h1>
        <div className="flex items-center space-x-3">
          <div className="flex items-center space-x-2">
            <Calendar className="w-4 h-4 text-gray-500" />
            <input
              type="date"
              value={dateRange.startDate}
              onChange={(e) => handleDateChange('startDate', e.target.value)}
              className="input text-sm"
            />
            <span className="text-gray-500">to</span>
            <input
              type="date"
              value={dateRange.endDate}
              onChange={(e) => handleDateChange('endDate', e.target.value)}
              className="input text-sm"
            />
          </div>
          <button className="btn-secondary flex items-center space-x-2">
            <Download className="w-4 h-4" />
            <span>Export</span>
          </button>
        </div>
      </div>

      {/* Monthly Summary */}
      {data.monthlySummary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total P&L</p>
                <p className={`text-2xl font-bold ${
                  data.monthlySummary.totalPnL >= 0 ? 'text-success-600' : 'text-danger-600'
                }`}>
                  ₹{data.monthlySummary.totalPnL.toLocaleString('en-IN')}
                </p>
              </div>
            </div>
          </div>
          
          <div className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Win Rate</p>
                <p className="text-2xl font-bold text-gray-900">
                  {data.monthlySummary.winRate.toFixed(1)}%
                </p>
              </div>
            </div>
          </div>
          
          <div className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Trades</p>
                <p className="text-2xl font-bold text-gray-900">
                  {data.monthlySummary.totalTrades}
                </p>
              </div>
            </div>
          </div>
          
          <div className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Avg P&L</p>
                <p className={`text-2xl font-bold ${
                  data.monthlySummary.avgPnL >= 0 ? 'text-success-600' : 'text-danger-600'
                }`}>
                  ₹{data.monthlySummary.avgPnL.toFixed(2)}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                activeTab === tab.id
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              <span>{tab.name}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'performance' && (
        <div className="space-y-6">
          {/* Performance Chart */}
          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Daily P&L Trend</h3>
            {data.performance.length > 0 ? (
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={data.performance}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="period" />
                    <YAxis />
                    <Tooltip 
                      formatter={(value) => [`₹${value.toFixed(2)}`, 'P&L']}
                      labelFormatter={(label) => `Date: ${label}`}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="totalPnL" 
                      stroke="#3b82f6" 
                      strokeWidth={2}
                      dot={{ fill: '#3b82f6' }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <BarChart3 className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>No performance data available</p>
              </div>
            )}
          </div>

          {/* Performance Table */}
          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Daily Performance</h3>
            {data.performance.length > 0 ? (
              <SortableTable
                data={data.performance}
                columns={[
                  { key: 'period', header: 'Date' },
                  { key: 'trades', header: 'Trades' },
                  { 
                    key: 'totalPnL', 
                    header: 'P&L',
                    render: (day) => (
                      <span className={day.totalPnL >= 0 ? 'text-success-600' : 'text-danger-600'}>
                        ₹{day.totalPnL.toFixed(2)}
                      </span>
                    )
                  },
                  { 
                    key: 'winRate', 
                    header: 'Win Rate',
                    render: (day) => `${day.winRate.toFixed(1)}%`
                  },
                  { 
                    key: 'totalVolume', 
                    header: 'Volume',
                    render: (day) => `₹${day.totalVolume.toLocaleString('en-IN')}`
                  }
                ]}
                searchPlaceholder="Search performance data..."
              />
            ) : (
              <div className="text-center py-8 text-gray-500">
                <p>No performance data available for the selected period</p>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'instruments' && (
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Instrument-wise Performance</h3>
          {data.instruments.length > 0 ? (
            <SortableTable
              data={data.instruments}
              columns={[
                { 
                  key: 'symbol', 
                  header: 'Symbol',
                  render: (instrument) => <span className="font-medium">{instrument.symbol}</span>
                },
                { key: 'totalTrades', header: 'Total Trades' },
                { 
                  key: 'totalPnL', 
                  header: 'Total P&L',
                  render: (instrument) => (
                    <span className={instrument.totalPnL >= 0 ? 'text-success-600' : 'text-danger-600'}>
                      ₹{instrument.totalPnL.toFixed(2)}
                    </span>
                  )
                },
                { 
                  key: 'winRate', 
                  header: 'Win Rate',
                  render: (instrument) => `${instrument.winRate.toFixed(1)}%`
                },
                { 
                  key: 'avgPnL', 
                  header: 'Avg P&L',
                  render: (instrument) => (
                    <span className={instrument.avgPnL >= 0 ? 'text-success-600' : 'text-danger-600'}>
                      ₹{instrument.avgPnL.toFixed(2)}
                    </span>
                  )
                },
                { 
                  key: 'totalVolume', 
                  header: 'Total Volume',
                  render: (instrument) => `₹${instrument.totalVolume.toLocaleString('en-IN')}`
                }
              ]}
              searchPlaceholder="Search instruments..."
            />
          ) : (
            <div className="text-center py-8 text-gray-500">
              <BarChart3 className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No instrument data available for the selected period</p>
            </div>
          )}
        </div>
      )}

      {activeTab === 'strategies' && (
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Strategy Performance</h3>
          {data.strategies.length > 0 ? (
            <SortableTable
              data={data.strategies}
              columns={[
                { 
                  key: 'strategy', 
                  header: 'Strategy',
                  render: (strategy) => (
                    <span className="font-medium">
                      {strategy.strategy.replace('_', ' ')}
                    </span>
                  )
                },
                { key: 'totalSignals', header: 'Total Signals' },
                { key: 'executedSignals', header: 'Executed' },
                { 
                  key: 'executionRate', 
                  header: 'Execution Rate',
                  render: (strategy) => `${strategy.executionRate.toFixed(1)}%`
                },
                { 
                  key: 'avgConfidence', 
                  header: 'Avg Confidence',
                  render: (strategy) => `${strategy.avgConfidence.toFixed(1)}%`
                }
              ]}
              searchPlaceholder="Search strategies..."
            />
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Filter className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No strategy data available for the selected period</p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default Reports